// Creator-controlled strings (token name/symbol, metadata) are arbitrary bytes onchain (HC-15).
// React renders them as text; on top of that we strip bidi overrides/isolates and control
// characters so a name cannot visually reorder surrounding UI, and only link safe URL schemes.
// (Character ranges are built from code points so this file stays plain ASCII.)

const range = (from: number, to: number) => `${String.fromCodePoint(from)}-${String.fromCodePoint(to)}`;
/** LRE, RLE, PDF, LRO, RLO and LRI, RLI, FSI, PDI */
const BIDI = new RegExp(`[${range(0x202a, 0x202e)}${range(0x2066, 0x2069)}]`, 'g');
/** C0 and C1 controls */
const CONTROLS = new RegExp(`[${range(0x00, 0x1f)}${range(0x7f, 0x9f)}]`, 'g');
/** the same, but keeping tab (0x09) and newline (0x0a) for free text */
const CONTROLS_EXCEPT_NEWLINE = new RegExp(`[${range(0x00, 0x08)}${range(0x0b, 0x1f)}${range(0x7f, 0x9f)}]`, 'g');

/** Removes bidi controls and control characters (use while typing; see cleanLabel). */
export const stripUnsafe = (s: string): string => s.replace(BIDI, '').replace(CONTROLS, '');

/** Names and symbols: no bidi controls, no control characters, trimmed. */
export const cleanLabel = (s: string | null | undefined): string => stripUnsafe(s ?? '').trim();

/** Free text (descriptions): no bidi controls; keeps newlines and tabs. */
export const cleanText = (s: string | null | undefined): string => (s ?? '').replace(BIDI, '').replace(CONTROLS_EXCEPT_NEWLINE, '');

function parse(u: string | null | undefined): URL | null {
  if (!u) return null;
  try {
    return new URL(u.trim());
  } catch {
    return null;
  }
}

/** Links from metadata: http(s) and ipfs only. */
export function safeLink(u: string | null | undefined): string | null {
  const url = parse(u);
  return url && (url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'ipfs:') ? url.toString() : null;
}

/** Images: http(s) only (browsers cannot load ipfs:// directly). */
export function safeImage(u: string | null | undefined): string | null {
  const url = parse(u);
  return url && (url.protocol === 'https:' || url.protocol === 'http:') ? url.toString() : null;
}
